// The demo console's frame (spec/24 §4.2, §4.3): everything between the real sign-in and the console's screens.
//
//   signed out          the console's own sign-in and sign-up pages, against the real api — nothing of the demo
//   signed in           the window is started (or read back), then one of:
//     running           the banner, and the console on the simulated venue (the store is handed to the backend)
//     ended             "Your 15-minute demo has ended", and when another can start — nothing of the venue
//     unreachable       a plain retry
//
// A change to the venue in another tab (the kiosk) invalidates the console's reads at once, so the Floor shows a
// sign-in at the kiosk within a moment rather than at its next poll.

import { formatDemoCountdown, type DemoWindowWire } from '@clavian/demo';
import { Button, DemoBar, Spinner, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from '@clavian/i18n/react';

import { useApi } from '../api/client';
import { queryKeys, useSession } from '../api/queries';
import { attachDemoStore, detachDemoStore } from './backend';
import { openBrowserDemoStore } from './browser-store';
import { DemoEnded } from './DemoEnded';
import { takeNextPage } from './next-page';
import { useDemoRuntime } from './runtime';
import { useDemoWindow, useDemoWindowStatus } from './use-demo-window';

import './demo.css';

/** Whether this page is one pane of the landing page's side-by-side view, which draws the one bar for both. */
const embedded = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export function DemoFrame({ children }: { readonly children: ReactNode }): ReactNode {
  const session = useSession();
  if (session.isPending) return <DemoLoading />;
  // Signed out (or the api unreachable): the console's own sign-in and sign-up pages.
  const user = session.data?.user;
  if (user === undefined) return children;
  // "Complete your profile" (a first Google, Discord or Steam sign-in lands here) comes before the demo: no window is
  // started and no remembered kiosk page is taken until the profile is saved, which then loads the page afresh.
  if (window.location.pathname === '/welcome') return children;
  return <DemoSignedIn userId={user.id}>{children}</DemoSignedIn>;
}

function DemoSignedIn({ userId, children }: { readonly userId: string; readonly children: ReactNode }): ReactNode {
  const api = useApi();
  // Signed in on the way to the kiosk or the side-by-side view (next-page.ts): go on there, once.
  const [next] = useState(takeNextPage);
  const { state, reload } = useDemoWindow(api, { start: true, enabled: next === null });
  const status = useDemoWindowStatus(state);

  useEffect(() => {
    if (next !== null) window.location.assign(next);
  }, [next]);
  if (next !== null) return <DemoLoading />;

  if (state.kind === 'loading' || status === null) return <DemoLoading />;
  if (state.kind === 'error') return <DemoUnreachable onRetry={() => reload(true)} />;
  if (state.window === null || status.kind === 'none') return <DemoUnreachable onRetry={() => reload(true)} />;
  if (status.kind === 'ended') {
    return <DemoEnded nextStartAt={status.nextStartAt} canStartAgain={status.canStartAgain} onStartAgain={() => reload(true)} />;
  }
  return (
    <DemoVenue userId={userId} window={state.window} secondsLeft={status.secondsLeft}>
      {children}
    </DemoVenue>
  );
}

function DemoVenue({
  userId,
  window: demoWindow,
  secondsLeft,
  children,
}: {
  readonly userId: string;
  readonly window: DemoWindowWire;
  readonly secondsLeft: number;
  readonly children: ReactNode;
}): ReactNode {
  const runtime = useDemoRuntime();
  const queryClient = useQueryClient();
  const { t } = useTranslation('demo');
  const toast = useToast();
  // One store per person per window: opened before the first render of the screens, so their first read finds it.
  const store = useMemo(() => {
    const opened = openBrowserDemoStore(userId, demoWindow);
    attachDemoStore(runtime, opened);
    return opened;
  }, [userId, demoWindow, runtime]);
  const [inFrame] = useState(embedded);

  useEffect(() => {
    attachDemoStore(runtime, store);
    // Another tab changed the venue (the kiosk): read again now rather than at the next poll. The session and the
    // window are the real api's and are left alone.
    const unsubscribe = store.subscribe(() => {
      void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== queryKeys.session[0] });
    });
    return () => {
      unsubscribe();
      store.close();
      detachDemoStore(runtime, store);
    };
  }, [store, runtime, queryClient]);

  const reset = () => {
    store.reset();
    void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== queryKeys.session[0] });
    toast.show({ tone: 'ok', title: t('banner.resetDone') });
  };

  return (
    <div className="demo-console">
      {inFrame ? null : (
        <DemoBar className="demo-console__bar" regionLabel={t('banner.region')} label={t('banner.label', { time: formatDemoCountdown(secondsLeft) })} urgent={secondsLeft <= 60}>
          <Button size="sm" variant="ghost" onClick={reset}>
            {t('banner.reset')}
          </Button>
          <a className="demo-console__link" href="/kiosk/" target="_blank" rel="noopener">
            {t('banner.openKiosk')}
          </a>
          <a className="demo-console__link" href="/">
            {t('banner.home')}
          </a>
        </DemoBar>
      )}
      {children}
    </div>
  );
}

function DemoLoading(): ReactNode {
  const { t } = useTranslation('demo');
  return (
    <main className="demo-page" aria-busy="true">
      <Spinner label={t('gate.loading')} />
    </main>
  );
}

function DemoUnreachable({ onRetry }: { readonly onRetry: () => void }): ReactNode {
  const { t } = useTranslation('demo');
  return (
    <main className="demo-page">
      <p role="alert">{t('gate.error')}</p>
      <Button onClick={onRetry}>{t('gate.retry')}</Button>
    </main>
  );
}
